<template>
  <v-skeleton-loader
    v-if="firstLoading"
    color="grey lighten-2"
    class="mx-auto"
    type="table-tbody"
  />
  <v-data-table 
    v-else
    :loading="loading"
    :headers="headers"
    :items="leaveApplications"
    :single-expand="true"
    show-expand
    item-key="id"
    class="elevation-1"
  >
    <template v-slot:top>
      <v-toolbar flat>
        <v-toolbar-title>Leave Applications</v-toolbar-title>
        <v-tooltip
          content-class="bottom"
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-avatar 
              class="cursor-pointer"
              v-on="on"
              @click="loadLeaveApplications"                
            >
              <v-icon medium>
                mdi-refresh
              </v-icon>
            </v-avatar>
          </template>
          <span>Reload</span>
        </v-tooltip>
      </v-toolbar>
    </template>
    <template v-slot:item.status="{ item }">
      <span
        :class="{ 
          'warning--text': (item.latest_status.id === 100),
          'red--text': (item.latest_status.id === 500), 
          'grey--text': (item.latest_status.id === 501), 
          'green--text': (item.latest_status.id === 200) 
        }"
      >
        {{ item.latest_status.name }}
      </span>
    </template>
    <template v-slot:item.approver="{ item }">
      <!-- display approvers in ascending order based on level  -->
      <span
        v-for="(approval, i) in item.approvals"
        :key="i"
        :class="[
          item.approvals.length > 1 && item.latest_status.id !== 501 ? { 
            'warning--text': (approval.status.id === 100),
            'red--text': (approval.status.id === 500), 
          } : ''
        ]"
      >
        <template v-if="approval.approver.employee_uid !== employeeUid">
          <span v-if="i !== 0">  ,</span>
          {{ (approval.approver.user || {}).name || approval.updated_by }}          
        </template>
      </span>
    </template>
    <template v-slot:item.action="{ item }">
      <employee-leaves-applications-popover-menu-cancel
        :leave-application-id="item.id"
        :disabled="
          (today >= item.start_date && item.latest_status.id === 200) ||
            item.latest_status.id === 500 || 
            item.latest_status.id === 501
        "
        @cancelled="cancel"
      />
    </template>
    <template v-slot:expanded-item="{ headers, item }">
      <td
        :colspan="headers.length"
        class="px-0"
      >        
        <v-container>          
          <v-row>
            <v-col cols="12">
              <v-card class="elevation-5">              
                <v-toolbar 
                  flat
                  color="white"
                >
                  <v-toolbar-title>Approval History</v-toolbar-title>
                </v-toolbar>
                <v-data-table 
                  :headers="approvalHeaders"
                  :items="item.approvals"
                  hide-default-footer
                  disable-pagination
                >
                  <template v-slot:item.status="{ item }">
                    <span
                      :class="{ 
                        'warning--text': (item.status.id === 100),
                        'red--text': (item.status.id === 500), 
                        'grey--text': (item.status.id === 501), 
                        'green--text': (item.status.id === 200) 
                      }"
                    >
                      {{ item.status.name }}
                    </span>
                  </template>
                  <template v-slot:item.date="{ item }">
                    {{ $moment(item.created_at).format('YYYY-MM-DD') }}
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>          
          <v-row>
            <v-col
              v-if="item.documents.length > 0"
              cols="12"
            >
              <v-card-subtitle class="font-weight-bold white black--text pb-0">
                {{ item.documents.length }} documents
              </v-card-subtitle>
            </v-col>
            
            <v-col
              v-for="document in item.documents"
              :key="document.id"
              cols="4"
              md="2"
              class="pt-0"
            >
              <v-chip
                color="blue-grey lighten-3"
                link
                style="width: 100%;"
                :href="document.file_url"
                target="_blank"
              >
                <v-icon
                  color="red"
                  left
                >
                  mdi-file-pdf
                </v-icon>
                <span class="text-truncate"> {{ document.file_name }} </span>
              </v-chip>
            </v-col>
          </v-row>
        </v-container>
      </td>
    </template>
  </v-data-table>
</template>

<script>
  export default {
    components: {
      EmployeeLeavesApplicationsPopoverMenuCancel: () => import('@/components/employee/leaves/applications/PopoverMenuCancel'),
    },
    props: {
      employeeUid: {
        type: String,
        default: '',
      },
    },
    data () {
      return {
        firstLoading: false,
        loading: false,
        headers: [
          {
            text: 'Type',
            align: 'left',
            value: 'leave_type',
          },
          {
            text: 'Start Date',
            align: 'left',
            value: 'start_date',
          },
          {
            text: 'End Date',
            align: 'left',
            value: 'end_date',
          },
          {
            text: 'Days',
            align: 'left',
            value: 'requested_days',
          },
          {
            text: 'Reason',
            align: 'left',
            sortable: false,
            value: 'reason',
          },
          {
            text: 'Status',
            align: 'left',
            sortable: false,
            value: 'status',
          },
          {
            text: 'Approvers',
            align: 'left',
            sortable: false,
            value: 'approver',
          },
          {
            text: 'Actions',
            align: 'left',
            sortable: false,
            value: 'action',
          },
        ],
        approvalHeaders: [
          {
            text: 'Approver',
            align: 'left',
            sortable: false,
            value: 'approver.user.name',
          },
          {
            text: 'Status',
            align: 'left',
            sortable: false,
            value: 'status',
          },
          {
            text: 'Date',
            align: 'left',
            sortable: false,
            value: 'date',
          },
          {
            text: 'Remark',
            align: 'left',
            sortable: false,
            value: 'remark',
          },
        ],
        leaveApplications: [],
      }
    },
    computed: {
      today () {
        return this.$moment().format('YYYY-MM-DD')
      },
    },
    watch: {
      employeeUid: {
        immediate: true,
        handler (value) {
          if (value) {
            this.firstLoading = true
            this.loadLeaveApplications()
          }
        },
      },
    },
    methods: {
      cancel () {
        this.loadLeaveApplications()
        this.$emit('cancelled')
      },
      loadLeaveApplications () {
        this.loading = true

        this.$api.leaveApplications().list(this.employeeUid)
          .then(response => {
            this.leaveApplications = []
            this.getLeaveApplications(response.data)            

            this.loading = false
            this.firstLoading = false
          })
      },
      getLeaveApplications (entitlements) {
        entitlements.map(entitlement => {
          entitlement.leave_applications.map(application => {
            let approvals = ((application.approval_request || {}).approvals || [])
            
            const sortedApprovals = _.orderBy(approvals, ['created_at'], ['asc'])
            const latestApproval = _.orderBy(approvals, ['created_at'], ['desc'])[0]

            this.leaveApplications.push({ 
              ...application, 
              latest_status: latestApproval.status, 
              leave_type: entitlement.leave_type.name, 
              approvals: sortedApprovals, 
            })
          })
        })

        this.leaveApplications = _.orderBy(this.leaveApplications, ['latest_status.id', 'created_at'], ['asc', 'desc'])
      },
    },
  }
</script>

<style scoped>
.cursor-pointer{
  cursor: pointer;
}
</style>
